import _ from 'lodash';

import * as utils from '../../common/utils';
import { INumericValue } from '../../interfaces/numeric-value.interface';
import { INumericDynamicCondition } from '../../interfaces/numeric-dynamic-condition.interface';
import { IWebSocketDeviceMessage } from '../../websockets/websocket-device-message.interface';
import { InOutInputPlugin } from '../in-out-input/in-out-input.plugin';

export class AnalogInputPlugin extends InOutInputPlugin
  implements INumericValue, INumericDynamicCondition {
  private static readonly MAX_DECIMALS = 2;
  private static readonly DEFAULT_CONFIG = {
    logScale: false,
    maxValue: 2.4,
    minValue: 0,
    rangeLower: 0,
    rangeUpper: 100,
    units: '%'
  };

  public getReportableAttrs() {
    return ['StatusFlags', 'PresentValue'];
  }

  /**
   * Conditions
   */

  public getConditionOptions() {
    return null;
  }

  public getConditionAttribute(): string | null {
    return 'PresentValue';
  }

  public getConditionMeta(value: number): object {
    return {
      processedValue: value
    };
  }

  public getConditionMin(): number {
    return this.getInputConfig().rangeLower;
  }

  public getConditionMax(): number {
    return this.getInputConfig().rangeUpper;
  }

  public getConditionInitial(): number {
    const min = this.getConditionMin();
    const max = this.getConditionMax();
    return Math.round((max - min) / 2);
  }

  private getInputConfig() {
    let minValue = AnalogInputPlugin.DEFAULT_CONFIG.minValue;
    let maxValue = AnalogInputPlugin.DEFAULT_CONFIG.maxValue;
    if (this.customOptions) {
      minValue =
        this.customOptions.minValue != null
          ? this.customOptions.minValue
          : minValue;
      maxValue =
        this.customOptions.maxValue != null
          ? this.customOptions.maxValue
          : maxValue;
    }

    return _.merge({}, AnalogInputPlugin.DEFAULT_CONFIG, this.findMetaInput(), {
      maxValue,
      minValue
    });
  }

  /**
   * Effects
   */

  public getEffectOptions() {}

  public getEffectAttribute(): string | null {
    return null;
  }

  public getEffectMethod(context?: any): string | null {
    return null;
  }

  /**
   * Numeric
   */

  public setValue(value: number, attribute?: string | undefined): void {}

  public getValue(attribute?: string | undefined): number {
    return this.state.presentValue;
  }

  public getUnit(attribute?: string | undefined): string {
    return this.getInputConfig().units;
  }

  /**
   * General
   */

  public getStatusPayload() {
    return ['PresentValue'];
  }

  public processMessage(message: IWebSocketDeviceMessage, timestamp?: number) {
    const response = message && message.data && message.data.response;
    const attribute = message && message.attributeOrMethod;

    if (attribute) {
      this.state[`${attribute[0].toLowerCase()}${attribute.substring(1)}`] =
        response.value;
      this._onUpdate.next({ state: this.state });
    }

    return this.state;
  }

  public getRawValue(value: any, attribute?: string, method?: string): any {
    let newValue = value;

    if (newValue != null) {
      const options = this.getInputConfig();

      newValue = utils.linearToLinear(
        newValue,
        options.rangeLower,
        options.rangeUpper,
        options.minValue,
        options.maxValue
      );

      if (options.logScale) {
        newValue = this.logToLinearScale(newValue, options);
      }
    }

    return newValue;
  }

  /**
   * Transforms a raw level value to a processed one
   * @param value Analog raw level value
   */
  public getProcessedValue(
    value: any,
    attribute?: string,
    method?: string
  ): any {
    let newValue = value;

    if (newValue != null) {
      const options = this.getInputConfig();

      if (options.logScale) {
        newValue = this.linearToLogScale(newValue, options);
      }

      newValue = utils.linearToLinear(
        newValue,
        options.minValue,
        options.maxValue,
        options.rangeLower,
        options.rangeUpper
      );

      newValue = _.round(newValue, AnalogInputPlugin.MAX_DECIMALS);
    }

    return newValue;
  }

  /**
   * Converts from a linear range of values to another range of values after log scaling
   * @param level Value to convert
   * @param options Options for the conversion
   */
  private linearToLogScale(
    level: number,
    options: {
      minValue: number;
      maxValue: number;
      rangeLower: number;
      rangeUpper: number;
    }
  ): number {
    const { minValue, maxValue, rangeLower, rangeUpper } = options;

    const range = maxValue - minValue;
    const linearValue = (level - minValue) / range;

    return utils.linearToLogarithmic(linearValue, minValue, maxValue);
  }

  /**
   * Converts from a log value to a linear value in a range
   * @param level Value to convert
   * @param options Options for the conversion
   */
  private logToLinearScale(
    level: number,
    options: {
      minValue: number;
      maxValue: number;
      rangeLower: number;
      rangeUpper: number;
    }
  ): number {
    const { minValue, maxValue } = options;

    const range = maxValue - minValue;
    const linearValue = utils.logarithmicToLinear(level, minValue, maxValue);

    return (linearValue * range) + minValue;
  }
}
